/**
 * Board 3 B · Mark quoted (460) — `docs/job-stages.md` §5, §8 B: a quote
 * that went out another way — handed over, printed, sent from someone's own
 * email. It runs Send's checks and job rule, starts *Valid until*, expires any
 * other version with the customer and moves the job to Quoted; no email, no
 * text (`sent_how = 'marked'`).
 *
 * **Which quote**: from the job, its drafts newest first with the first
 * picked; from a draft's ⋯ (`quoteId`), only that draft. **How it went out**
 * is optional. The green band says what it does. `POST /quotes/:id/mark-quoted`.
 */
import { useEffect, useState } from 'react'
import type { JobActionsAnswer, JobStagesQuoteRow, MarkedHow } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { formatDateOnly, formatMoneyText } from '../lib/format.js'
import { markQuoted, readJobActions } from './api.js'
import { MARK_QUOTED, SENT_HOW, quoteVersion } from './copy.js'
import {
  ChipRow, FieldLabel, Fields, PillChip, RadioDot, Refusal, StageBand, StagePopup, messageOf,
} from './popup.js'
import { markQuotedReady, markQuotedRows } from './rules.js'

export interface MarkQuotedProps {
  open: boolean
  jobId: string
  /** From a draft's ⋯: only that draft. Left out (from the job): the job's drafts, newest first. */
  quoteId?: string | null
  onClose: () => void
  /** After the server marked it: the quote that went out, and how. */
  onDone: (answer: { quoteId: string; how: MarkedHow | null }) => void
  phone?: boolean
}

export function MarkQuoted(props: MarkQuotedProps) {
  if (!props.open) return null
  return <MarkQuotedBox {...props} />
}

function MarkQuotedBox({ jobId, quoteId, onClose, onDone, phone }: MarkQuotedProps) {
  const [facts, setFacts] = useState<JobActionsAnswer | null>(null)
  const [picked, setPicked] = useState<string | null>(null)
  const [how, setHow] = useState<MarkedHow | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    readJobActions(jobId, quoteId ?? undefined)
      .then((answer) => {
        if (!live) return
        setFacts(answer)
        setPicked(markQuotedRows(answer.quotes, quoteId)[0]?.id ?? null)
      })
      .catch((cause: unknown) => { if (live) setError(messageOf(cause)) })
    return () => { live = false }
  }, [jobId, quoteId])

  const rows: JobStagesQuoteRow[] = facts ? markQuotedRows(facts.quotes, quoteId) : []
  const ready = markQuotedReady({ quoteId: picked, how })

  async function mark() {
    if (!picked) return
    setBusy(true)
    setError(null)
    try {
      await markQuoted(picked, how)
      onDone({ quoteId: picked, how })
    } catch (cause) {
      setError(messageOf(cause))
    } finally {
      setBusy(false)
    }
  }

  const band = MARK_QUOTED.band(facts?.firstName)

  return (
    <StagePopup
      width={460}
      title={MARK_QUOTED.title}
      {...(facts ? { sub: MARK_QUOTED.sub(facts.job.reference) } : {})}
      tone="info"
      glyph="send"
      onClose={onClose}
      asDrawn="js-03 B"
      {...(phone === undefined ? {} : { phone })}
      buttons={[
        { label: MARK_QUOTED.cancel, tone: 'plain', onClick: onClose },
        { label: MARK_QUOTED.markQuoted, tone: 'primary', glyph: 'send', disabled: busy || !ready, onClick: () => void mark() },
      ]}
    >
      {facts
        ? (
          <Fields>
            <div className="min-w-0">
              <FieldLabel id="mark-quoted-which" label={MARK_QUOTED.which} required />
              <div role="radiogroup" aria-labelledby="mark-quoted-which"
                className="overflow-hidden rounded-card border border-line">
                {rows.map((row) => {
                  const on = row.id === picked
                  return (
                    <button
                      key={row.id}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      onClick={() => setPicked(row.id)}
                      className={cn('flex w-full items-center gap-[11px] border-b border-line-faint px-3 py-2.5 text-left tabular-nums last:border-b-0',
                        on ? 'bg-green-band-bg' : 'bg-surface hover:bg-hover')}
                    >
                      <RadioDot on={on} />
                      <span className="flex min-w-0 flex-1 flex-col">
                        <b className="text-[13px]">{quoteVersion(row.reference, row.version)}</b>
                        <span className="text-[11.5px] text-text-muted">
                          {MARK_QUOTED.draft(formatDateOnly(row.createdOn ?? row.createdAt), row.createdByName)}
                        </span>
                      </span>
                      <b className="shrink-0 text-[13px]">{formatMoneyText(row.total)}</b>
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="min-w-0">
              <FieldLabel id="mark-quoted-how" label={MARK_QUOTED.how} optional />
              <ChipRow labelledBy="mark-quoted-how">
                {(Object.keys(SENT_HOW) as MarkedHow[]).map((one) => (
                  <PillChip key={one} label={SENT_HOW[one]} on={how === one} onClick={() => setHow(how === one ? null : one)} />
                ))}
              </ChipRow>
            </div>

            <StageBand tone="green">
              <b>{band.lead}</b> {band.before}<i>{band.validUntil}</i>{band.after}
            </StageBand>

            <Refusal message={error} />
          </Fields>
        )
        : <Refusal message={error} />}
    </StagePopup>
  )
}
